import { DateTime } from 'luxon';
import React, {
  useRef,
  useState,
  useMemo,
  FunctionComponent,
  ReactElement,
} from 'react';
import { FormCheck, Row, Modal, Button, Table } from 'react-bootstrap';
import apis from '../../apis';
import {
  HeaderDisplayKeysWithType,
  InlineEditableTable,
} from '../../components';
import {
  PHISight,
  Semester,
  Student,
  StudentPHIAll,
  YearSemClasses,
} from '../../model';
import {
  SightTableHeader,
  denormalizeStudentSight,
  normalizeStudentSight,
  PHISightCalculators,
} from '../TableHeaders/SightHeader';
import ApexCharts from 'apexcharts';
import ExcelJS from 'exceljs';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { downloadBlob } from '../../utils/element';
import { toast } from 'react-toastify';

type Props = {
  editable: boolean;
  sights: PHISight[];
  student: StudentPHIAll;
  schoolId: string;
  semesters: Semester[];
  yearSems: YearSemClasses[];
  onApiError: (e: any) => void;
  onValueUpdated: () => void;
};

const inlineSightTableHeader: HeaderDisplayKeysWithType<PHISight>[] = [
  { property: 'year', display: '學年' },
  { property: 'sem', display: '學期' },
  ...Object.values(SightTableHeader),
];
const manageIDMap: Record<string, string> = {
  '1': '長效散瞳劑\n(阿托平 Atropine)',
  '2': '短效散瞳劑',
  '3': '其他藥物',
  '4': '配鏡矯治',
  '5': '更換鏡片',
  '6': '遮眼治療',
  '7a': '配戴隱形眼鏡(軟式)',
  '7b': '配戴隱形眼鏡(硬式)',
  '7c': '配戴隱形眼鏡(軟、硬式)',
  '8': '角膜塑型片',
  '9': '視力保健衛教',
  '10': '其他',
  '11': '定期檢查',
};

export const SightSection: FunctionComponent<Props> = ({
  editable,
  sights,
  student,
  schoolId,
  semesters,
  yearSems,
  onApiError,
  onValueUpdated,
}) => {
  const [inputing, setInputing] = useState(false);
  const currentSemester = semesters.find((s) => s.isNow);
  const sight = useMemo(
    () =>
      sights.find(
        (s) => s.sem === currentSemester?.sem && s.year === currentSemester.year
      ),
    [sights, currentSemester]
  );
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartReady, setChartReady] = useState(false);

  const sortedHistory = useMemo(
    () =>
      [...sights].sort(
        (a, b) =>
          (a.classGrade ?? 0) * 10 + a.sem - ((b.classGrade ?? 0) * 10 + b.sem)
      ),
    [sights]
  );
  const numberToChinese = (num: number) =>
    ['零', '一', '二', '三', '四', '五', '六'][num] ?? num.toString();
  const chartCategories = sortedHistory.map((item) => {
    const grade = item.classGrade ?? 0;
    return grade === 0 ? '－' : `${numberToChinese(grade)}-${item.sem}`;
  });

  const nowYearSem = yearSems.find(
    (s) => s.sem === currentSemester?.sem && s.year === currentSemester?.year
  );
  const sightChartOptions: ApexOptions = {
    chart: {
      id: 'sight-chart',
      width: 1200,
      toolbar: { show: false },
      animations: {
        enabled: false,
      },
    },
    title: {
      text: '視力發展圖',
      align: 'center',
      style: { fontSize: '18px', fontWeight: 'bold' },
    },
    xaxis: { title: { text: '年級學期' }, categories: chartCategories },
    yaxis: { title: { text: '換算後近視度數（估值）' } },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      itemMargin: { horizontal: 10 },
    },
    colors: [
      '#1E90FF',
      '#1E90FF', // 裸視
      '#32CD32',
      '#32CD32', // 戴鏡
      '#800080',
      '#800080', // 近視
      '#FFA500',
      '#FFA500', // 遠視
      '#FF0000',
      '#FF0000', // 散光
      '#00CED1',
      '#00CED1', // 屈光
    ],
    stroke: {
      width: [3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3],
      curve: 'smooth',
      dashArray: [0, 4, 0, 4, 0, 4, 0, 4, 0, 4, 0, 4],
      lineCap: 'round',
    },
    markers: {
      size: 5,
      strokeWidth: 2,
      strokeColors: '#fff',
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} 度`,
      },
    },
    grid: { show: true },
    dataLabels: { enabled: false },
  };

  function convertVAtoDiopter(input: any): number | null {
    const va = typeof input === 'number' ? input : parseFloat(input);
    if (isNaN(va)) return null;
    if (va >= 1.0) return 0;
    if (va >= 0.8) return -50;
    if (va >= 0.6) return -100;
    if (va >= 0.4) return -200;
    if (va >= 0.2) return -300;
    if (va >= 0.1) return -500;
    return -600; // VA < 0.1
  }

  const sightChartSeries = [
    {
      name: '裸視(右)',
      data: sortedHistory.map((s) => convertVAtoDiopter(s.sight0R)),
      marker: { shape: 'circle' },
    },
    {
      name: '裸視(左)',
      data: sortedHistory.map((s) => convertVAtoDiopter(s.sight0L)),
      marker: { shape: 'square' },
    },
    {
      name: '戴鏡(右)',
      data: sortedHistory.map((s) => convertVAtoDiopter(s.sightR)),
      marker: { shape: 'circle' },
    },
    {
      name: '戴鏡(左)',
      data: sortedHistory.map((s) => convertVAtoDiopter(s.sightL)),
      marker: { shape: 'square' },
    },
    {
      name: '近視(右)',
      data: sortedHistory.map((s) => s.eNearR ?? null),
      marker: { shape: 'circle' },
    },
    {
      name: '近視(左)',
      data: sortedHistory.map((s) => s.eNearL ?? null),
      marker: { shape: 'square' },
    },
    {
      name: '遠視(右)',
      data: sortedHistory.map((s) => s.eFarR ?? null),
      marker: { shape: 'circle' },
    },
    {
      name: '遠視(左)',
      data: sortedHistory.map((s) => s.eFarL ?? null),
      marker: { shape: 'square' },
    },
    {
      name: '散光(右)',
      data: sortedHistory.map((s) => s.eSanR ?? null),
      marker: { shape: 'circle' },
    },
    {
      name: '散光(左)',
      data: sortedHistory.map((s) => s.eSanL ?? null),
      marker: { shape: 'square' },
    },
    {
      name: '屈光(右)',
      data: sortedHistory.map((s) => s.diopR ?? null),
      marker: { shape: 'circle' },
    },
    {
      name: '屈光(左)',
      data: sortedHistory.map((s) => s.diopL ?? null),
      marker: { shape: 'square' },
    },
  ];

  async function getImageSizeFromDataURL(dataURL: string) {
    return new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.src = dataURL;
    });
  }

  async function handleExportSightExcel() {
    setChartReady(true);
    await new Promise((r) => setTimeout(r, 800));

    const imageData = await createSightChartImage(sortedHistory);

    if (!imageData) {
      toast.error('產生圖表失敗');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('視力歷程');

    sheet.mergeCells('A1:L1');
    sheet.getCell(
      'A1'
    ).value = `${nowYearSem?.grade}年${nowYearSem?.no}班${student.seat}號${student.name}視力發展歷程`;
    sheet.getCell('A1').alignment = { horizontal: 'center' };
    sheet.getCell('A1').font = { bold: true, size: 14 };

    sheet.addRow([
      '學期',
      '裸視(右)',
      '裸視(左)',
      '戴鏡(右)',
      '戴鏡(左)',
      '近視(右)',
      '近視(左)',
      '遠視(右)',
      '遠視(左)',
      '散光(右)',
      '散光(左)',
      '屈光(右)',
      '屈光(左)',
    ]);

    sortedHistory.forEach((sight) => {
      sheet.addRow([
        `${numberToChinese(sight.classGrade ?? 0)}-${sight.sem}`,
        formatSightValue(sight.sight0R),
        formatSightValue(sight.sight0L),
        formatSightValue(sight.sightR),
        formatSightValue(sight.sightL),
        formatSightValue(sight.eNearR),
        formatSightValue(sight.eNearL),
        formatSightValue(sight.eFarR),
        formatSightValue(sight.eFarL),
        formatSightValue(sight.eSanR),
        formatSightValue(sight.eSanL),
        formatSightValue(sight.diopR),
        formatSightValue(sight.diopL),
      ]);
    });

    function formatSightValue(val?: number | null): string {
      if (val === null || val === undefined) return '';

      switch (val) {
        case -1:
          return '視力值＜0.1';
        case -5:
          return '<0.1(全盲)';
        case -6:
          return '雷射治療';
        case -7:
          return '角膜塑型';
        case -8:
          return '戴隱形眼鏡';
        case -9:
          return '無法表達或在家自學';
      }

      const num = Number(val);

      if (Number.isInteger(num)) {
        return num.toFixed(1); // 1 → 1.0
      }

      return num.toString(); // 0.8 → 0.8
    }

    const imgSize = await getImageSizeFromDataURL(imageData);
    const displayWidth = 640;
    const scale = displayWidth / imgSize.width;
    const displayHeight = imgSize.height * scale;

    const imageId = workbook.addImage({ base64: imageData, extension: 'png' });
    sheet.addImage(imageId, {
      tl: { col: 0, row: sortedHistory.length + 4 },
      ext: { width: 1400, height: 900 },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(
      blob,
      `${nowYearSem?.grade}年${nowYearSem?.no}班${student.seat}號${student.name}視力發展歷程.xlsx`
    );
  }

  async function createSightChartImage(
    history: PHISight[]
  ): Promise<string> {
    const width = 1400;
    const height = 900;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('無法建立Canvas');
    }

    const context = ctx;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);

    const margin = {
      top: 100,
      left: 120,
      right: 120,
      bottom: 120,
    };

    const chartWidth =
      width - margin.left - margin.right;

    const chartHeight =
      height - margin.top - margin.bottom;

    const labels = history.map(
      (item) =>
        `${numberToChinese(item.classGrade ?? 0)}-${item.sem}`
    );

    const leftSeries = [
      ...history.flatMap((s) => [
        s.eNearR,
        s.eNearL,
        s.eFarR,
        s.eFarL,
        s.eSanR,
        s.eSanL,
        s.diopR,
        s.diopL,
      ]),
    ].filter((x): x is number => x !== null && x !== undefined);

    const rightSeries = [
      ...history.flatMap((s) => [
        s.sight0R,
        s.sight0L,
        s.sightR,
        s.sightL,
      ]),
    ]
      .filter((x): x is number => x !== null && x !== undefined)
      .map((v) => (v < 0 ? 0 : v));

    const leftMin = Math.min(...leftSeries, -100);
    const leftMax = Math.max(...leftSeries, 100);

    const rightMin = 0;
    const rightMax = 2;

    function getX(index: number) {
      if (history.length <= 1) {
        return margin.left + chartWidth / 2;
      }

      return (
        margin.left +
        (index * chartWidth) / (history.length - 1)
      );
    }

    function getLeftY(value: number) {
      return (
        margin.top +
        chartHeight -
        ((value - leftMin) /
          (leftMax - leftMin)) *
        chartHeight
      );
    }

    function getRightY(value: number) {
      return (
        margin.top +
        chartHeight -
        ((value - rightMin) /
          (rightMax - rightMin)) *
        chartHeight
      );
    }

    context.strokeStyle = '#000';
    context.lineWidth = 1;

    context.beginPath();
    context.moveTo(margin.left, margin.top);
    context.lineTo(
      margin.left,
      margin.top + chartHeight
    );
    context.stroke();

    context.beginPath();
    context.moveTo(
      width - margin.right,
      margin.top
    );
    context.lineTo(
      width - margin.right,
      margin.top + chartHeight
    );
    context.stroke();

    context.beginPath();
    context.moveTo(
      margin.left,
      margin.top + chartHeight
    );
    context.lineTo(
      width - margin.right,
      margin.top + chartHeight
    );
    context.stroke();

    context.font = '18px Arial';
    context.fillStyle = '#000';

    labels.forEach((label, i) => {
      const x = getX(i);

      context.save();
      context.translate(
        x,
        margin.top + chartHeight + 40
      );
      context.rotate(-0.5);
      context.fillText(label, 0, 0);
      context.restore();
    });

    // 左右Y軸刻度與網格線
    context.font = '14px Arial';

    const tickCount = 6;

    for (let i = 0; i < tickCount; i++) {
      const ratio = i / (tickCount - 1);
      const y = margin.top + chartHeight * ratio;

      const leftValue =
        leftMax - (leftMax - leftMin) * ratio;

      const rightValue =
        rightMax - (rightMax - rightMin) * ratio;

      // 網格線
      context.strokeStyle = '#d0d0d0';
      context.lineWidth = 1;

      context.beginPath();
      context.moveTo(margin.left, y);
      context.lineTo(width - margin.right, y);
      context.stroke();

      // 左Y軸數值
      context.fillStyle = '#000';
      context.textAlign = 'right';
      context.textBaseline = 'middle';

      context.fillText(
        leftValue.toFixed(0),
        margin.left - 10,
        y
      );

      // 右Y軸數值
      context.textAlign = 'left';

      context.fillText(
        rightValue.toFixed(1),
        width - margin.right + 10,
        y
      );
    }

    function drawLegend(
      x: number,
      y: number,
      color: string,
      text: string,
      dashed = false
    ) {
      context.strokeStyle = color;
      context.fillStyle = color;
      context.lineWidth = 3;

      context.setLineDash(
        dashed ? [8, 4] : []
      );

      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(x + 25, y);
      context.stroke();

      context.setLineDash([]);

      context.beginPath();
      context.arc(
        x + 12,
        y,
        4,
        0,
        Math.PI * 2
      );
      context.fill();

      context.fillStyle = '#000';
      context.font = '14px Arial';
      context.textAlign = 'left';
      context.textBaseline = 'middle';

      context.fillText(
        text,
        x + 35,
        y
      );
    }

    function drawLine(
      data: (number | null | undefined)[],
      color: string,
      useRightAxis = false,
      dashed = false
    ) {
      context.strokeStyle = color;
      context.lineWidth = 3;
      context.setLineDash(
        dashed ? [8, 4] : []
      );
      let started = false;

      data.forEach((value, i) => {
        if (
          value === null ||
          value === undefined
        ) {
          return;
        }

        const x = getX(i);

        const y = useRightAxis
          ? getRightY(value < 0 ? 0 : value)
          : getLeftY(value);

        if (!started) {
          context.beginPath();
          context.moveTo(x, y);
          started = true;
        } else {
          context.lineTo(x, y);
        }
      });

      context.stroke();
      context.setLineDash([]);

      data.forEach((value, i) => {
        if (
          value === null ||
          value === undefined
        ) {
          return;
        }

        const x = getX(i);

        const y = useRightAxis
          ? getRightY(value < 0 ? 0 : value)
          : getLeftY(value);

        context.beginPath();
        context.fillStyle = color;
        context.arc(x, y, 6, 0, Math.PI * 2);
        context.fill();
      });
    }
    drawLegend(180, 70, '#800080', '近視(右)');
    drawLegend(320, 70, '#800080', '近視(左)', true);

    drawLegend(460, 70, '#008000', '遠視(右)');
    drawLegend(600, 70, '#008000', '遠視(左)', true);

    drawLegend(740, 70, '#ff6600', '散光(右)');
    drawLegend(880, 70, '#ff6600', '散光(左)', true);

    drawLegend(180, 95, '#00bcd4', '屈光(右)');
    drawLegend(320, 95, '#00bcd4', '屈光(左)', true);

    drawLegend(460, 95, '#ff0000', '裸視(右)');
    drawLegend(600, 95, '#ff0000', '裸視(左)', true);

    drawLegend(740, 95, '#0066ff', '戴鏡(右)');
    drawLegend(880, 95, '#0066ff', '戴鏡(左)', true);

    drawLine(history.map((s) => s.sight0R), '#ff0000', true);
    drawLine(history.map((s) => s.sight0L), '#ff0000', true, true);

    drawLine(history.map((s) => s.sightR), '#0066ff', true);
    drawLine(history.map((s) => s.sightL), '#0066ff', true, true);

    drawLine(history.map((s) => s.eNearR), '#800080');
    drawLine(history.map((s) => s.eNearL), '#800080', false, true);

    drawLine(history.map((s) => s.eFarR), '#008000');
    drawLine(history.map((s) => s.eFarL), '#008000', false, true);

    drawLine(history.map((s) => s.eSanR), '#ff6600');
    drawLine(history.map((s) => s.eSanL), '#ff6600', false, true);

    drawLine(history.map((s) => s.diopR), '#00bcd4');
    drawLine(history.map((s) => s.diopL), '#00bcd4', false, true);

    context.font = '28px Arial';
    context.fillStyle = '#000';
    context.textAlign = 'center';

    context.fillText(
      '視力發展圖',
      width / 2,
      50
    );

    return canvas.toDataURL('image/png');
  }

  function onValueUpdate({
    periodical,
    ...value
  }: PHISight & { periodical?: any }) {
    if((value.eFarR && value.eNearR ) ||  (value.eFarL && value.eNearL)) {
      toast.error('同一隻眼睛不能同時輸入遠視力與近視力');
      return;
    }

    if (value.manageID?.length) {
      const sortedManageIds = [...value.manageID].sort((a, b) =>
        a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );

      const id = sortedManageIds[0];
      value.manage = manageIDMap[id];
    }
    apis
      .InsertStudentSight(student.pid, {
        ...value,
        periodical,
        examDate: DateTime.now(),
      })
      .catch(onApiError)
      .then(onValueUpdated);
  }

  function onValueDelete(value: PHISight) {
    apis
      .deleteStudentSight(student.pid, value.year, value.sem)
      .catch(onApiError)
      .then(onValueUpdated);
  }

  function formatSight(value?: number | string | null): string {
    if (typeof value === 'number') {
      if (value === -1) return '<0.1';
      if (value === -5) return '<0.1\n(全盲)';
      if (value < 0) return value.toString();
      return value.toFixed(1);
    }
    return value ?? '';
  }

  yearSems
    .filter((s: YearSemClasses) =>
      !sights.some((sight) => sight.sem === s.sem && sight.year === s.year)
    )
    .forEach(({ year, sem, yearClassId }: YearSemClasses) =>
      sights.push({
        sem,
        year,
        yearClassId,
        id: 0,
        eFar: false,
        eNear: false,
        eSan: false,
        eWeak: false,
        eSight99: false,
        isDilated: false,
        isDilating: false,
        noProblem: false,
        manageID: [],
      })
    );

  sights.sort((a, b) => a.sem - b.sem + (a.year - b.year) * 100);
  return (
    <>
      <div className="sheet-title">
        視力
        <Button
          variant="link"
          size="sm"
          className="px-2 py-1 text-decoration-none"
          onClick={async () => {
            if (sights.length === 0) {
              toast.warning('無視力資料，無法匯出');
              return;
            }
            await handleExportSightExcel();
            toast.success('視力發展歷程已匯出');
          }}
        >
          歷程
        </Button>
        <Button
          key="cell-edit"
          className="border-0 px-2 py-1"
          variant="outline-primary"
          size="sm"
          onClick={() => {
            setInputing(true);
          }}
        >
          <span className="feather icon-edit" />
        </Button>
      </div>
      <Table bordered className="student-phi">
        <tbody>
          <tr>
            <th>裸視</th>
            <td>
              右：{formatSight(sight?.sight0R)} 左：
              {formatSight(sight?.sight0L)}{' '}
            </td>
          </tr>
          <tr>
            <th>戴鏡</th>
            <td>
              右：{formatSight(sight?.sightR)} 左：{formatSight(sight?.sightL)}
            </td>
          </tr>
          <tr>
            <th>屈光度數</th>
            <td>
              右：{sight?.diopR} 左：{sight?.diopL}
            </td>
          </tr>
          <tr>
            <th>近視</th>
            <td>
              右：{sight?.eNearR} 左：{sight?.eNearL}
            </td>
          </tr>
          <tr>
            <th>遠視</th>
            <td>
              右：{sight?.eFarR} 左：{sight?.eFarL}
            </td>
          </tr>
          <tr>
            <th>散光</th>
            <td>
              右：{sight?.eSanR} 左：{sight?.eSanL}
            </td>
          </tr>
          <tr>
            <th>症狀</th>
            <td>
              <Row className="m-0">
                {generateCheckBox('近', sight?.eNear)}
                {generateCheckBox('遠', sight?.eFar)}
                {generateCheckBox('弱', sight?.eWeak)}
                {generateCheckBox('散', sight?.eSan)}
                {generateCheckBox('其他', sight?.eSight99)}
              </Row>
            </td>
          </tr>
          <tr>
            <th>處置</th>
            <td>
              {sight?.manageID
                ? sight?.manageID.map((v) => manageIDMap[v]).join('、')
                : ''}
            </td>
          </tr>
          <tr>
            <th>備註</th>
            <td>
              {sight?.eSight99State}
            </td>
          </tr>
        </tbody>
      </Table>
      <Modal
        show={inputing}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        dialogClassName="modal-dialog-full"
        centered
      >
        <Modal.Header closeButton onHide={() => setInputing(false)}>
          <Modal.Title id="contained-modal-title-vcenter">
            視力數據 -{' '}
            <strong>
              {student.seat} {student.name}
            </strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <div>
            視力請直接輸入整數，例如 0.8 輸入 8 視力值＜0.1請輸入-1
            <br />
            「散瞳治療」是指點長效型散瞳眼藥治療，「散瞳」是指檢驗屈光度數前的短效散瞳。
            <br />
            裸眼視力無法測量代號說明：-8 戴隱形眼鏡；-7 角膜塑型；-6
            雷射治療；-5 全盲
            <br />
            無法表達或在家自學僅輸入裸視右眼為 -9
            <br />
            「定期檢查」日期格式：1999/01/01 或點兩下顯示萬年曆選單
            <br />
            如「近視右（左）」或「遠視右（左）」有小數點，請將小數點四捨五入至個位數後以整數輸入
            <br />
          </div>
          <div className='mt-1 mb-3 d-flex'>
            <div style={{ backgroundColor: '#cacaff', width: '25px', height: '25px' }} ></div>
            此顏色代表初檢輸入欄位
          </div>
          <InlineEditableTable
            decorators={PHISightCalculators}
            values={sights}
            headers={inlineSightTableHeader}
            onValueUpdate={onValueUpdate}
            onValueDelete={onValueDelete}
            onRowRender={(v) => ({
              editable:
                editable &&
                (!v.schoolId || schoolId === v.schoolId) &&
                semesters.some((s) => s.sem === v.sem && s.year === v.year),
              deleteable:
                (!v.schoolId || schoolId === v.schoolId) &&
                semesters.some((s) => s.sem === v.sem && s.year === v.year),
            })}
            beforeEdit={({ id, yearClassId, year, sem, ...s }) => ({
              id,
              year,
              sem,
              yearClassId,
              ...denormalizeStudentSight(s),
            })}
            afterEdit={(s) => ({ ...s, ...normalizeStudentSight(s) })}
          />
        </Modal.Body>
      </Modal>
      <div
        ref={chartRef}
        style={{ position: 'absolute', top: -10000, left: -10000, opacity: 0 }}
      >
        {chartReady && (
          <Chart
            options={sightChartOptions}
            series={sightChartSeries}
            type="line"
            height={900}
            width={1400}
          />
        )}
      </div>
    </>
  );
};

function generateCheckBox(
  label: string,
  value: boolean | undefined
): ReactElement {
  return (
    <div>
      {label}
      <FormCheck inline readOnly checked={!!value} />
    </div>
  );
}
